import { deleteRequestWithBody, getRequest, postRequest, putRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import type Company from '../models/company';
import { type DeletionOutcome, deletionErrorKey, type RetirementImpact } from '../../offices/services/offices.service';
import showStatusCodeError from './logging.service';
import { dataCache, COMPANIES_CACHE_KEY } from '../../../core/utils/data-cache';

export const getCompanies = async (keycloak: KeycloakContext): Promise<Company[] | undefined> => {
  const apiResponse = await getRequest<Company[]>('company', keycloak);
  if (apiResponse.error) {
    return undefined;
  }

  return apiResponse.json;
};

export const createCompany = async (company: Company, keycloak: KeycloakContext): Promise<Company | undefined> => {
  const apiResponse = await postRequest<Company>('company', keycloak, company);
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  // Invalidate the cached companies list so the next visit fetches fresh data.
  dataCache.invalidate(COMPANIES_CACHE_KEY);
  return apiResponse.json;
};

export const updateCompany = async (company: Company, keycloak: KeycloakContext): Promise<Company | undefined> => {
  const apiResponse = await putRequest<Company>('company', keycloak, company);
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  // Invalidate the cached companies list to ensure updated data is reflected.
  dataCache.invalidate(COMPANIES_CACHE_KEY);
  return apiResponse.json;
};

export interface CompanyDeletionImpact {
  readonly companyId: string;
  readonly companyName: string;
  readonly impact: RetirementImpact;
}

export const getCompanyDeletionImpact = async (
  id: string,
  keycloak: KeycloakContext,
): Promise<CompanyDeletionImpact | undefined> => {
  const apiResponse = await getRequest<CompanyDeletionImpact>(`company/${id}/deletion-impact`, keycloak);

  if (apiResponse.error) {
    return undefined;
  }

  return apiResponse.json;
};

/**
 * Deletes a company and retires its offices, acknowledging what that cancels. The server refuses with 409 when
 * there is an impact and the flag is absent.
 */
export const deleteCompanyById = async (id: string, keycloak: KeycloakContext, acknowledged = false): Promise<DeletionOutcome> => {
  const apiResponse = await deleteRequestWithBody('company', keycloak, { id, acknowledged });

  if (apiResponse.error) {
    return { deleted: false, errorKey: deletionErrorKey(apiResponse.status, 'delete-company-error-message') };
  }

  // Invalidate the cached companies list so the deleted company is no longer shown.
  dataCache.invalidate(COMPANIES_CACHE_KEY);
  return { deleted: true };
};
